import { inject, Injectable } from "@angular/core";
import { filter, map, of, switchMap, tap } from "rxjs";
import { SessionApiService } from "@app/shared/general/sessions/services/session-api.service";
import { DefaultNotificationApiService } from "@app/kernel/notifications/notification.config";
import { AuthTokenService } from "../../../auth/services/auth-token.service";
import { AuthService } from "../../../auth/services/auth.service";

/**
 * Extended Notification API service that overrides the default implementation (`DefaultNotificationApiService`)
 * to integrate Firebase Cloud Messaging (FCM) token registration with session management.
 *
 * This implementation ensures that FCM tokens are securely associated with user sessions only when
 * the user is authenticated. It also synchronizes updated session information with the authentication layer.
 *
 * ## Key Differences from DefaultNotificationApiService
 * - Overrides `addToken` to link the FCM token with the active session via the (`SessionApiService`).
 * - Synchronizes the updated session with the local authentication service (`AuthService`).
 * - Provides a stub for `deleteToken`, No-op, as removal is handled via `addToken(null)`
 */
@Injectable()
export class NotificationApiService extends DefaultNotificationApiService {
  private sessionApiService = inject(SessionApiService);
  private tokenService = inject(AuthTokenService);
  private authService = inject(AuthService);

  /**
   * Associates the FCM token with the current authenticated session.
   *
   * If the user is authenticated:
   * - Sends a GraphQL mutation to store the token in the session.
   * - Applies the updated session locally.
   *
   * @param fcmToken The Firebase Cloud Messaging token to associate with the session.
   * @returns An observable that completes after session update.
   */
  override addToken(fcmToken: string | null) {
    const canUpdateFcmToken =
      this.tokenService.hasValidTokenSnapshot() &&
      this.tokenService.isAssociatedWithSessionSnapshot() &&
      this.authService.getCurrentSession()?.fcmToken != fcmToken;

    return of(canUpdateFcmToken).pipe(
      filter(isAuthed => isAuthed),
      switchMap(() =>
        this.sessionApiService.updateMySessionFcmToken(fcmToken).pipe(
          filter(session => !!session),
          tap(session => this.authService.patchActiveSessionDetails(session)),
          map(() => void 0)
        )
      )
    );
  }

  /**
   * Token removal is handled by passing `null` to `addToken()`.
   *
   * This method is intentionally a no-op override to comply with the abstract interface.
   *
   * @param _fcmToken The FCM token intended for removal (not used).
   * @returns A completed observable indicating no further action is taken.
   */
  override deleteToken(_fcmToken: string) {
    return of(void 0);
  }
}
